// src/pages/characters.jsx
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import './characters.css';
import Nav from '../components/layout/nav';
import Settings from '../components/layout/Settings';
import { useSound } from '../hooks/useSound';
import { useProgress } from '../hooks/useProgress';

/* ── Character roster ──────────────────────────────────────────────────────── */
const CHARACTERS = [
  {
    id:          'prisma',
    name:        'PRISMA',
    description: 'The cheerful leader of the Prism Pals. Loves sorting colors and helping others see the bright side.',
    color:       '#FF0078',
    glow:        '#FF0078',
    cssClass:    'charcard-prisma',
    unlockLevel: 1,
  },
  {
    id:          'glowzy',
    name:        'GLOWZY',
    description: 'The cosmic thinker. Glowzy loves riddles, logic puzzles, and gazing at distant star clusters.',
    color:       '#18B8E8',
    glow:        '#18B8E8',
    cssClass:    'charcard-glowzy',
    unlockLevel: 2,
  },
  {
    id:          'lumix',
    name:        'LUMIX',
    description: 'The energetic speedster. Lumix moves at the speed of light and always finishes puzzles first.',
    color:       '#B5E917',
    glow:        '#B5E917',
    cssClass:    'charcard-lumix',
    unlockLevel: 5,
  },
  {
    id:          'vortex',
    name:        'VORTEX',
    description: 'The mysterious one. Vortex speaks in riddles and has a strange magnetic pull on all things neon.',
    color:       '#9B4EC4',
    glow:        '#9B4EC4',
    cssClass:    'charcard-vortex',
    unlockLevel: 10,
  },
  {
    id:          'flare',
    name:        'FLARE',
    description: 'Fiery and fierce, Flare burns through obstacles and lights up any room (sometimes literally).',
    color:       '#FF7A1A',
    glow:        '#FF7A1A',
    cssClass:    'charcard-flare',
    unlockLevel: 15,
  },
  {
    id:          'nova',
    name:        'NOVA',
    description: 'Born from a supernova, Nova radiates pure energy and can perceive patterns invisible to others.',
    color:       '#FFD930',
    glow:        '#FFD930',
    cssClass:    'charcard-nova',
    unlockLevel: 20,
  },
  {
    id:          'glitch',
    name:        'GLITCH',
    description: 'Unpredictable and chaotic, Glitch bends the rules of the prism world — but always for good.',
    color:       '#1AD9C4',
    glow:        '#1AD9C4',
    cssClass:    'charcard-glitch',
    unlockLevel: 30,
  },
  {
    id:          'apex',
    name:        'APEX',
    description: 'The ultimate Prism Pal. Apex only reveals themselves to those who have mastered all 50 levels.',
    color:       '#FFFFFF',
    glow:        '#C0C0C0',
    cssClass:    'charcard-apex',
    unlockLevel: 50,
  },
];

/* ── Component ─────────────────────────────────────────────────────────────── */
const Characters = () => {
  const navigate = useNavigate();
  const { playHover, playClick, playLock } = useSound();
  const { progress } = useProgress();
  const gridRef = useRef(null);
  const titleRef = useRef(null);

  const isUnlocked = (unlockLevel) => progress.unlockedLevel >= unlockLevel;

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo(titleRef.current,
      { opacity: 0, y: -20 },
      { opacity: 1, y: 0, duration: 0.6 }
    );

    const cards = gridRef.current?.querySelectorAll('.char-card');
    if (cards) {
      tl.fromTo(cards,
        { opacity: 0, y: 30, scale: 0.92 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, stagger: 0.07 },
        '-=0.2'
      );
    }
  }, []);

  const handleCardClick = (char) => {
    if (isUnlocked(char.unlockLevel)) {
      playClick();
    } else {
      playLock();
    }
  };

  return (
    <main className="characters">
      <Nav title="CHARACTERS" sub={`${CHARACTERS.filter(c => isUnlocked(c.unlockLevel)).length} / ${CHARACTERS.length} Unlocked`} />

      <div className="chars-inner">
        <div className="chars-subtitle" ref={titleRef}>
          Complete levels to unlock more Prism Pals
        </div>

        <div className="chars-grid" ref={gridRef}>
          {CHARACTERS.map((char) => {
            const unlocked = isUnlocked(char.unlockLevel);
            return (
              <div
                key={char.id}
                className={`char-card ${unlocked ? 'char-card--unlocked' : 'char-card--locked'}`}
                onClick={() => handleCardClick(char)}
                onMouseEnter={playHover}
                style={unlocked ? {
                  '--char-color': char.color,
                  '--char-glow':  char.glow,
                  borderColor:    `${char.color}55`,
                  boxShadow:      `0 0 0 1px ${char.color}22, 0 0 32px ${char.color}18`,
                } : {}}
              >
                {/* Artwork */}
                <div className={`char-art ${char.cssClass} ${!unlocked ? 'char-art--locked' : ''}`}>
                  {!unlocked && (
                    <div className="char-lock-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </div>
                  )}
                  {unlocked && (
                    <div className="char-art-glow" style={{ background: `radial-gradient(ellipse, ${char.glow}33 0%, transparent 70%)` }} />
                  )}
                </div>

                {/* Info */}
                <div className="char-info">
                  <h3
                    className="char-name"
                    style={unlocked ? { color: char.color, textShadow: `0 0 12px ${char.glow}88` } : {}}
                  >
                    {unlocked ? char.name : '???'}
                  </h3>

                  {unlocked ? (
                    <p className="char-desc">{char.description}</p>
                  ) : (
                    <p className="char-desc char-desc--locked">
                      Complete Level {char.unlockLevel} to unlock
                    </p>
                  )}

                  <div className={`char-badge ${unlocked ? 'char-badge--unlocked' : 'char-badge--locked'}`}>
                    {unlocked ? '✓ UNLOCKED' : `🔒 LVL ${char.unlockLevel}`}
                  </div>
                </div>

                {/* Hover glow border */}
                {unlocked && (
                  <div className="char-card-hover-glow" style={{ background: `radial-gradient(ellipse at 50% 100%, ${char.glow}22 0%, transparent 60%)` }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Settings />
    </main>
  );
};

export default Characters;